import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';

import { get as userGet } from 'js/lib/user';

import LegacyGetS12nCertBannerWrapper from 'bundles/composable-page-header-consumer/banners/LegacyGetS12nCertBannerWrapper';
import LegacyProductDiscountPromoBannerWrapper from 'bundles/composable-page-header-consumer/banners/LegacyProductDiscountPromoBannerWrapper';
import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import ConsumerSimpleDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/ConsumerSimpleDesktopMainNav';
import DegreeLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/DegreeLearnerOnConsumerDesktopMainNav';
import EnterpriseLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/EnterpriseLearnerOnConsumerDesktopMainNav';
import LoggedInConsumerNoSubNavDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/LoggedInConsumerNoSubNavDesktopMainNav';
import LoggedOutConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/LoggedOutConsumerDesktopMainNav';
import OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav';
import ConsumerSimpleMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/ConsumerSimpleMobileMainNav';
import DegreeLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/DegreeLearnerOnConsumerMobileMainNav';
import EnterpriseLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/EnterpriseLearnerOnConsumerMobileMainNav';
import LoggedInConsumerWithMetaNavLinksMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/LoggedInConsumerWithMetaNavLinksMobileMainNav';
import LoggedOutConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/LoggedOutConsumerMobileMainNav';
import OrgScopedEnterpriseLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/OrgScopedEnterpriseLearnerOnConsumerMobileMainNav';
import DefaultConsumerPageHeaderProvider from 'bundles/composable-page-header-consumer/providers/DefaultConsumerPageHeaderProvider';
import GlobalPageHeaderContainer from 'bundles/composable-page-header/components/GlobalPageHeaderContainer';
import DefaultMetaNavigation from 'bundles/composable-page-header/components/meta-nav/DefaultMetaNavigation';
import { DEFAULT_META_NAV_HEIGHT } from 'bundles/composable-page-header/constants';
import GlobalPageHeaderStateProvider from 'bundles/composable-page-header/providers/GlobalPageHeaderStateProvider';
import type { GetS12nCertificateBannerProps, ProductDiscountPromoBannerProps } from 'bundles/page/types/Program';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

type Props = {
  productDiscountPromoBannerProps?: ProductDiscountPromoBannerProps;
  getS12nCertificateBannerProps?: GetS12nCertificateBannerProps;
  showSimpleHeader?: boolean;
  // Forwarded to GlobalPageHeaderContainer's outer .rc-GlobalPageHeaderContainer div so callers
  // (e.g. ConsumerHeaderFooterWrapper) can measure the header's transformed rect during the
  // smart-header translateY animation.
  headerRef?: React.RefObject<HTMLDivElement>;
};

const ProductDescriptionPageHeader = ({
  productDiscountPromoBannerProps,
  getS12nCertificateBannerProps,
  showSimpleHeader,
  headerRef,
}: Props) => {
  const { chosenEnterpriseProgram, chosenDegree, naptimeChosenOrganization } = useConsumerMyPageHeader();
  const user = userGet();
  const isOrgScoped = useIsOrgScopedHome();

  const getDesktopNav = () => {
    if (showSimpleHeader) {
      return (
        <ConsumerSimpleDesktopMainNav
          enableCourseraLogoLink
          showProgramSwitcher
          showLanguageDropdown
          showCoach
          showAcctDropdown
          showLoginBtn={false}
        />
      );
    }

    if (isOrgScoped && user?.id && !!naptimeChosenOrganization) {
      return <OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav />;
    } else if (user?.id && !!chosenEnterpriseProgram) {
      return <EnterpriseLearnerOnConsumerDesktopMainNav />;
    } else if (user?.id && !!chosenDegree) {
      return <DegreeLearnerOnConsumerDesktopMainNav />;
    } else if (user?.id) {
      return <LoggedInConsumerNoSubNavDesktopMainNav />;
    } else {
      return <LoggedOutConsumerDesktopMainNav />;
    }
  };
  const getMobileNav = () => {
    if (showSimpleHeader) {
      return (
        <ConsumerSimpleMobileMainNav
          enableCourseraLogoLink
          showProgramSwitcher
          showLanguageDropdown
          showAcctDropdown
          showLoginBtn={false}
        />
      );
    }

    if (user?.id && !!chosenDegree) {
      return <DegreeLearnerOnConsumerMobileMainNav />;
    } else if (isOrgScoped && user?.id && !!naptimeChosenOrganization) {
      return <OrgScopedEnterpriseLearnerOnConsumerMobileMainNav />;
    } else if (user?.id && !!chosenEnterpriseProgram) {
      return <EnterpriseLearnerOnConsumerMobileMainNav />;
    } else if (user?.id) {
      return <LoggedInConsumerWithMetaNavLinksMobileMainNav />;
    } else {
      return <LoggedOutConsumerMobileMainNav />;
    }
  };

  const desktopNavComponent = getDesktopNav();
  const mobileNavComponent = getMobileNav();

  return (
    <GlobalPageHeaderContainer
      ref={headerRef}
      desktopMainNav={desktopNavComponent}
      mobileMainNav={mobileNavComponent}
      metaNav={!showSimpleHeader ? <DefaultMetaNavigation /> : undefined}
      scrollMovementOffset={{
        desktop: !showSimpleHeader ? DEFAULT_META_NAV_HEIGHT : 0,
        mobile: 0,
      }}
      banners={[
        {
          name: 'ProductDiscountPromoBanner',
          component: (
            <LegacyProductDiscountPromoBannerWrapper
              productDiscountPromoBannerProps={productDiscountPromoBannerProps}
            />
          ),
        },
        {
          name: 'GetS12nCertBanner',
          component: <LegacyGetS12nCertBannerWrapper getS12nCertificateBannerProps={getS12nCertificateBannerProps} />,
        },
      ]}
    />
  );
};

const ProductDescriptionPageHeaderWithProvider = ({
  productDiscountPromoBannerProps,
  getS12nCertificateBannerProps,
  showSimpleHeader,
  headerRef,
}: Props) => {
  return (
    <StatsigProvider>
      <GlobalPageHeaderStateProvider
        additionalDefaultPageHeaderHeight={!showSimpleHeader ? DEFAULT_META_NAV_HEIGHT : 0}
      >
        <DefaultConsumerPageHeaderProvider>
          <ProductDescriptionPageHeader
            productDiscountPromoBannerProps={productDiscountPromoBannerProps}
            getS12nCertificateBannerProps={getS12nCertificateBannerProps}
            showSimpleHeader={showSimpleHeader}
            headerRef={headerRef}
          />
        </DefaultConsumerPageHeaderProvider>
      </GlobalPageHeaderStateProvider>
    </StatsigProvider>
  );
};

export default ProductDescriptionPageHeaderWithProvider;
